(() => {
  const SELECTOR = '.pdp-yotpo-drawer-reviews__content[data-yotpo-auth-url][data-yotpo-reviews-url]';

  const MOCK_PAYLOAD = {
    response: {
      bottomline: {
        average_score: 0,
        total_review: 0,
        custom_fields_bottomline: {},
      },
      reviews: [],
    },
  };

  const formatDate = (isoString) => {
    try {
      const d = new Date(isoString);
      return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (_) {
      return '';
    }
  };

  const escapeHtml = (str) => {
    if (str == null) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
  };

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  const renderStars = (score, max = 5) => {
    const s = clamp(Number(score) || 0, 0, max);
    const full = Math.floor(s);
    const half = s - full >= 0.5 ? 1 : 0;
    const empty = max - full - half;

    const star = (type) => {
      if (type === 'full') return '<span class="pdp-yotpo-star pdp-yotpo-star--full">★</span>';
      if (type === 'half') return '<span class="pdp-yotpo-star pdp-yotpo-star--half">★</span>';
      return '<span class="pdp-yotpo-star pdp-yotpo-star--empty">★</span>';
    };

    return `${Array.from({ length: full }).map(() => star('full')).join('')}${
        half ? star('half') : ''
    }${Array.from({ length: empty }).map(() => star('empty')).join('')}`;
  };

  const normalizeFitLabel = (fitValue) => {
    const v = Number(fitValue);
    if (!Number.isFinite(v)) return '';
    if (v <= 1) return 'Runs Small';
    if (v === 2) return 'True to size';
    return 'Runs Large';
  };

  const normalizeQualityLabel = (qValue) => {
    const v = Number(qValue);
    if (!Number.isFinite(v)) return '';
    if (v <= 2) return 'Low';
    if (v === 3) return 'Standard';
    return 'High';
  };

  const renderRange = ({ title, minLabel, midLabel, maxLabel, value, min, max, showMid = true, tickCount }) => {
    const v = clamp(Number(value) || 0, min, max);
    const pct = ((v - min) / (max - min)) * 100;

    const resolvedTickCount = Number.isFinite(Number(tickCount)) ? Number(tickCount) : null;
    const ticks = (() => {
      if (resolvedTickCount && resolvedTickCount >= 2) {
        const selectedIdx = clamp(
            Math.round(((v - min) / (max - min)) * (resolvedTickCount - 1)),
            0,
            resolvedTickCount - 1
        );
        return Array.from({ length: resolvedTickCount })
            .map((_, idx) => {
              const tPct = (idx / (resolvedTickCount - 1)) * 100;
              const active = idx === selectedIdx ? ' is-active' : '';
              return `<span class="pdp-yotpo-range__tick${active}" style="left:${tPct}%"></span>`;
            })
            .join('');
      }

      const stepCount = Math.max(0, Math.floor(max) - Math.ceil(min));
      const selectedStep = clamp(Math.round(v), Math.ceil(min), Math.floor(max));
      return Array.from({ length: stepCount + 1 })
          .map((_, idx) => {
            const t = Math.ceil(min) + idx;
            const tPct = ((t - min) / (max - min)) * 100;
            const active = t === selectedStep ? ' is-active' : '';
            return `<span class="pdp-yotpo-range__tick${active}" style="left:${tPct}%"></span>`;
          })
          .join('');
    })();

    return `
      <div class="pdp-yotpo-range">
        <div class="pdp-yotpo-range__title F1-Reg-LC">${escapeHtml(title)}</div>
        <div class="pdp-yotpo-range__track" role="meter" aria-valuemin="${min}" aria-valuemax="${max}" aria-valuenow="${v}">
          ${ticks}
          <div class="pdp-yotpo-range__marker" style="left:${pct}%"></div>
        </div>
        <div class="pdp-yotpo-range__labels F2-Reg-UC">
          <span>${escapeHtml(minLabel)}</span>
          ${showMid && midLabel ? `<span>${escapeHtml(midLabel)}</span>` : ''}
          <span>${escapeHtml(maxLabel)}</span>
        </div>
      </div>
    `;
  };

  const getToken = async (authUrl) => {
    const cacheKey = `pdp_yotpo_token_${authUrl}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return cached;

    const res = await fetch(authUrl, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Auth failed: ${res.status}`);
    const data = await res.json();
    const token = data.access_token || data.token || data?.response?.access_token || data?.response?.token;
    if (!token) throw new Error('Auth token missing in response');
    sessionStorage.setItem(cacheKey, token);
    return token;
  };

  const getReviews = async ({ reviewsUrl, appKey, productId, token, page, perPage }) => {
    const url = new URL(reviewsUrl, window.location.origin);
    if (productId) url.searchParams.set('product_id', String(productId));
    if (appKey) url.searchParams.set('app_key', String(appKey));
    if (page) url.searchParams.set('page', String(page));
    if (perPage) url.searchParams.set('per_page', String(perPage));

    const res = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: token ? { 'X-Yotpo-Token': token } : undefined,
    });
    if (!res.ok) throw new Error(`Reviews fetch failed: ${res.status}`);
    return res.json();
  };

  const getMockPayload = async ({ container }) => {
    const url = container.getAttribute('data-yotpo-mock-payload-url');
    if (!url) return MOCK_PAYLOAD;

    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) return MOCK_PAYLOAD;
    const json = await res.json();
    if (!json || typeof json !== 'object') return MOCK_PAYLOAD;
    return json;
  };

  const parseList = (raw) => {
    if (!raw) return null;
    const s = String(raw).trim();
    if (!s) return null;

    // JSON array support
    if (s.startsWith('[')) {
      try {
        const arr = JSON.parse(s);
        if (Array.isArray(arr)) return arr.map((x) => String(x).trim()).filter(Boolean);
      } catch (_) {
        return null;
      }
    }

    // Comma-separated support
    return s
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean);
  };

  const renderSummary = (summaryRoot, payload, config) => {
    const response = payload?.response;
    const bottomline = response?.bottomline;

    if (!bottomline) {
      summaryRoot.innerHTML = '';
      return;
    }

    const avgScore = Number(bottomline.average_score || 0).toFixed(1);
    const totalReviews = Number(bottomline.total_review || 0);

    // Calculate aggregate slider values from all reviews
    const reviews = Array.isArray(response?.reviews) ? response.reviews : [];
    const fitMatchTitle = config?.fit?.title || 'Fit Expectation';
    const qualityMatchTitle = config?.quality?.title || 'Quality';

    let fitSum = 0;
    let fitCount = 0;
    let qualitySum = 0;
    let qualityCount = 0;

    reviews.forEach((review) => {
      const cf = review.custom_fields || {};
      const cfEntries = Object.values(cf);
      const getCfValue = (title) => cfEntries.find((x) => x?.title === title)?.value;

      const fitValue = getCfValue(fitMatchTitle);
      const qualityValue = getCfValue(qualityMatchTitle);

      if (fitValue !== undefined && fitValue !== null) {
        fitSum += Number(fitValue);
        fitCount++;
      }
      if (qualityValue !== undefined && qualityValue !== null) {
        qualitySum += Number(qualityValue);
        qualityCount++;
      }
    });

    const avgFit = fitCount > 0 ? fitSum / fitCount : null;
    const avgQuality = qualityCount > 0 ? qualitySum / qualityCount : null;

    summaryRoot.innerHTML = `
      <div class="pdp-yotpo-summary">
        <div class="pdp-yotpo-summary__rating">
          <div class="pdp-yotpo-summary__stars">${renderStars(avgScore)}</div>
          <span class="pdp-yotpo-summary__score F1-Medium-LC">${avgScore}</span>
          <span class="pdp-yotpo-summary__count F1-Reg-LC">(${totalReviews} review${totalReviews !== 1 ? 's' : ''})</span>
        </div>

        ${
        avgFit !== null
            ? renderRange({
              title: config?.fit?.title || fitMatchTitle,
              minLabel: config?.fit?.minLabel || 'Runs Small',
              midLabel: config?.fit?.midLabel || 'True to size',
              maxLabel: config?.fit?.maxLabel || 'Runs Large',
              showMid: config?.fit?.showMid,
              tickCount: config?.fit?.tickCount,
              value: avgFit,
              min: 0,
              max: 3,
            })
            : ''
    }

        ${
        avgQuality !== null
            ? renderRange({
              title: config?.quality?.title || qualityMatchTitle,
              minLabel: config?.quality?.minLabel || 'Low',
              midLabel: config?.quality?.midLabel || 'Standard',
              maxLabel: config?.quality?.maxLabel || 'High',
              showMid: config?.quality?.showMid,
              tickCount: config?.quality?.tickCount,
              value: avgQuality,
              min: 0,
              max: 5,
            })
            : ''
    }
      </div>
    `;
  };

  const renderReviewItem = (review, config, index) => {
    const fitMatchTitle = config?.fit?.title || 'Fit Expectation';
    const qualityMatchTitle = config?.quality?.title || 'Quality';

    const cf = review.custom_fields || {};
    const cfEntries = Object.values(cf);
    const getCfValue = (title) => cfEntries.find((x) => x?.title === title)?.value;

    const height = getCfValue('Height');
    const sizePurchased = getCfValue('Size Purchased');
    const usualSize = getCfValue('Usual Size?');

    const fitValue = getCfValue(fitMatchTitle);
    const qualityValue = getCfValue(qualityMatchTitle);

    const customerInfo = [
      height ? `<span class="F1-Reg-LC">Height: ${escapeHtml(height)}</span>` : '',
      sizePurchased ? `<span class="F1-Reg-LC">Size purchased: ${escapeHtml(sizePurchased)}</span>` : '',
      usualSize ? `<span class="F1-Reg-LC">Usual Size: ${escapeHtml(usualSize)}</span>` : '',
    ]
        .filter(Boolean)
        .join('  ');

    return `
      <div class="yotpo-review-item" data-review-index="${index}">
        <div class="yotpo-review-header">
          <div class="yotpo-review-user">
            <span class="yotpo-review-name F1-Medium-LC">${escapeHtml(review.user?.display_name || '')}, ${escapeHtml(formatDate(review.created_at))}</span>
          </div>
          <span class="yotpo-review-verified F1-Medium-LC">${review.verified_buyer ? 'Verified Buyer' : ''}</span>
        </div>

        <div class="yotpo-review-rating">
          <div class="pdp-yotpo-stars">${renderStars(review.score)}</div>
          <span class="yotpo-rating-number">${Number(review.score || 0).toFixed(1)}</span>
        </div>

        <div class="yotpo-review-content">
          <p class="yotpo-review-text F1-Reg-LC">${escapeHtml(review.content || '')}</p>
        </div>

        ${
        customerInfo
            ? `<div class="yotpo-customer-info">${customerInfo}</div>`
            : ''
    }

        <div class="yotpo-review-ranges">
          ${
        fitValue
            ? renderRange({
              title: config?.fit?.title || fitMatchTitle,
              minLabel: config?.fit?.minLabel || 'Runs Small',
              midLabel: config?.fit?.midLabel || 'True to size',
              maxLabel: config?.fit?.maxLabel || 'Runs Large',
              showMid: config?.fit?.showMid,
              tickCount: config?.fit?.tickCount,
              value: Number(fitValue),
              min: 0,
              max: 3,
            })
            : ''
    }

          ${
        qualityValue
            ? renderRange({
              title: config?.quality?.title || qualityMatchTitle,
              minLabel: config?.quality?.minLabel || 'Low',
              midLabel: config?.quality?.midLabel || 'Standard',
              maxLabel: config?.quality?.maxLabel || 'High',
              showMid: config?.quality?.showMid,
              tickCount: config?.quality?.tickCount,
              value: Number(qualityValue),
              min: 0,
              max: 5,
            })
            : ''
    }
        </div>
      </div>
    `;
  };

  const render = (root, reviews, config, { visibleCount, totalReview, onShowMore }) => {
    if (!reviews.length) {
      root.innerHTML = '';
      return;
    }

    const visible = reviews.slice(0, visibleCount);
    const hasMore = visibleCount < totalReview;

    root.innerHTML = visible.map((review, idx) => renderReviewItem(review, config, idx + 1)).join('')
        + (hasMore
            ? '<div class="pdp-yotpo-show-more-wrapper" style="padding: 48px 0;"><button type="button" class="secondary-button" style="width: auto; background: transparent;" data-yotpo-show-more>More Reviews</button></div>'
            : '');

    if (hasMore) {
      const btn = root.querySelector('[data-yotpo-show-more]');
      if (btn) btn.addEventListener('click', onShowMore);
    }
  };

  const init = async (container) => {
    const root = container.querySelector('[data-yotpo-reviews]');
    const summaryRoot = container.querySelector('[data-yotpo-summary]');
    if (!root) return;

    const authUrl = container.getAttribute('data-yotpo-auth-url');
    const reviewsUrl = container.getAttribute('data-yotpo-reviews-url');
    const appKey = container.getAttribute('data-yotpo-app-key');
    const productId = container.getAttribute('data-yotpo-product-id');

    const fitList = parseList(container.getAttribute('data-yotpo-fit'));
    const qualityList = parseList(container.getAttribute('data-yotpo-quality'));

    const listToConfig = (arr, defaults) => {
      if (!Array.isArray(arr) || arr.length < 3) return defaults;

      // Support both formats:
      // - [min, mid, max] (or any length labels list)
      // - [title, ...labels]
      //
      // Disambiguation: treat the first element as a title ONLY when it matches
      // the expected Yotpo field title (defaults.title). Otherwise, assume it's
      // a labels-only list.
      const first = String(arr[0] || '').trim();
      const hasTitle =
          arr.length >= 4 &&
          first &&
          String(defaults.title || '').trim().toLowerCase() === first.toLowerCase();

      const title = hasTitle ? first : defaults.title;
      const labels = hasTitle ? arr.slice(1) : arr;

      if (labels.length < 2) return defaults;
      const minLabel = labels[0];
      const maxLabel = labels[labels.length - 1];

      const showMid = labels.length % 2 === 1;
      const midLabel = showMid ? labels[Math.floor(labels.length / 2)] : '';

      return { title, minLabel, midLabel, maxLabel, showMid, tickCount: labels.length };
    };

    const sliderConfig = {
      fit: listToConfig(fitList, {
        title: 'Fit Expectation',
        minLabel: 'Runs Small',
        midLabel: 'True to size',
        maxLabel: 'Runs Large',
        tickCount: 4,
      }),
      quality: listToConfig(qualityList, {
        title: 'Quality',
        minLabel: 'Low',
        midLabel: 'Standard',
        maxLabel: 'High',
        tickCount: 6,
      }),
    };

    const qs = new URLSearchParams(window.location.search);
    const mockEnabled =
        container.getAttribute('data-yotpo-mock') === 'true' ||
        container.hasAttribute('data-yotpo-mock') ||
        qs.get('yotpo_mock') === '1';

    // Pagination state
    const state = {
      allReviews: [],
      visibleCount: 0,
      totalReview: 0,
      currentPage: 1,
      token: null,
      loading: false,
    };

    const SHOW_MORE_COUNT = 3;

    const rerender = () => {
      render(root, state.allReviews, sliderConfig, {
        visibleCount: state.visibleCount,
        totalReview: state.totalReview,
        onShowMore: handleShowMore,
      });
    };

    const handleShowMore = async () => {
      if (state.loading) return;

      const nextVisible = state.visibleCount + SHOW_MORE_COUNT;

      // Fetch next page if we need more reviews than we have
      if (nextVisible > state.allReviews.length && state.allReviews.length < state.totalReview) {
        state.loading = true;
        try {
          state.currentPage++;
          const payload = await getReviews({
            reviewsUrl, appKey, productId,
            token: state.token,
            page: state.currentPage,
          });
          const newReviews = Array.isArray(payload?.response?.reviews) ? payload.response.reviews : [];
          state.allReviews = state.allReviews.concat(newReviews);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
        } finally {
          state.loading = false;
        }
      }

      state.visibleCount = Math.min(nextVisible, state.allReviews.length);
      rerender();
    };

    const initWithPayload = (payload) => {
      const response = payload?.response;
      state.allReviews = Array.isArray(response?.reviews) ? response.reviews : [];
      state.totalReview = Number(response?.bottomline?.total_review || 0);
      state.visibleCount = state.allReviews.length;

      if (summaryRoot) renderSummary(summaryRoot, payload, sliderConfig);
      rerender();
    };

    if (mockEnabled) {
      try {
        root.classList.add('is-loading');
        const payload = await getMockPayload({ container });
        initWithPayload(payload);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        root.classList.remove('is-loading');
      }
      return;
    }

    try {
      root.classList.add('is-loading');
      state.token = await getToken(authUrl);
      const payload = await getReviews({ reviewsUrl, appKey, productId, token: state.token });
      initWithPayload(payload);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      root.classList.remove('is-loading');
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(SELECTOR).forEach((el) => {
      init(el);
    });
  });

  // Preserve scroll position for review drawer - override close method
  const initScrollPreservation = () => {
    const reviewDrawer = document.querySelector('global-drawer-pdpdrawerreviews');
    if (!reviewDrawer) {
      setTimeout(initScrollPreservation, 100);
      return;
    }

    let savedScrollPosition = 0;

    // Save original close method
    const originalClose = reviewDrawer.close.bind(reviewDrawer);

    // Override close method
    reviewDrawer.close = function() {
      // Save scroll position before anything happens
      savedScrollPosition = window.scrollY || window.pageYOffset;
      console.log('Intercepted close, saving scroll:', savedScrollPosition);

      // Call original close
      originalClose();

      // Restore scroll position immediately and repeatedly
      const restoreScroll = () => window.scrollTo(0, savedScrollPosition);

      restoreScroll();
      requestAnimationFrame(restoreScroll);
      setTimeout(restoreScroll, 0);
      setTimeout(restoreScroll, 10);
      setTimeout(restoreScroll, 50);
      setTimeout(restoreScroll, 100);
      setTimeout(restoreScroll, 150);
      setTimeout(restoreScroll, 200);
      setTimeout(restoreScroll, 250);
      setTimeout(restoreScroll, 300);
    };

    // Also save position when drawer opens
    window.addEventListener('global-drawer-opened', (event) => {
      if (event.detail.drawerId === 'PdpDrawerReviews') {
        savedScrollPosition = window.scrollY || window.pageYOffset;
        console.log('Drawer opened, saved scroll:', savedScrollPosition);
      }
    });
  };

  // Initialize after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initScrollPreservation);
  } else {
    initScrollPreservation();
  }
})();
